import { MenuTitles } from 'constants/menu';
import { ContentLayout, PageLayout } from 'layout';
import { useContext, useId } from 'react';
import { AgxSpacer, AgxTypography, Icon_Email, Icon_Link } from '@urbanx/agx.ui-components2';
import { CampaignTabHeader } from 'components';
import { useSelectedAgency } from 'hooks';
import './activityLog.scss';
import { useParams } from 'react-router-dom';
import { AuthContext } from '@urbanx/agx.auth.client';
import {
  IActivityLogResponseAction,
  useGetCampaignActivityLogs,
} from '@urbanx/agx.activities.client';
import LoadingState from 'components/loadingState/LoadingState';
import { tokens } from '@urbanx/agx.ui-components2/tokens';
import dayjs from 'dayjs';

const actionLabels: Record<IActivityLogResponseAction, string> = {
  Added: 'Added',
  Created: 'Created',
  Generated: 'Generated',
  Obtained: 'Obtained',
  Updated: 'Updated',
  Completed: 'Completed',
  Converted: 'Converted',
  Uploaded: 'Uploaded',
  SentEmail: 'Sent Email',
  SentViaDocuSign: 'Sent Via Docusign',
  FailedToSend: 'Failed To Send',
  Archived: 'Archived',
  DisconnectedRexListing: 'Disconnected',
};

const ActivityLogView = () => {
  const pageId = useId();
  const { userAccount } = useContext(AuthContext);
  const { campaignId } = useParams();
  const { agency } = useSelectedAgency();
  const {
    data: activityLogs,
    isPending,
    isError,
  } = useGetCampaignActivityLogs(campaignId ?? '', !!campaignId);

  return (
    <PageLayout
      agentName={userAccount?.firstName || ''}
      primaryPageTitle={agency?.name || ''}
      currentPageTitle={MenuTitles.ACTIVITY}
      isCampaignsSection
    >
      <ContentLayout hasSecondaryMenu={false} secondaryActiveMenu={MenuTitles.ACTIVITY} isCampaigns>
        <AgxSpacer gap="spacing-md" fill>
          <CampaignTabHeader title="Activity" titleId="lblActivity" />
          {isPending && <LoadingState />}
          {isError && (
            <AgxTypography id="activity-load-error" type="body" size="small">
              Unable to load campaign activity.
            </AgxTypography>
          )}
          <AgxSpacer gap="spacing-3xl" padding={{ top: 'spacing-4xl', bottom: 'spacing-4xl' }} fill>
            {activityLogs?.map((activity, index) => {
              const id = `${pageId}-${index}`;
              const disconnected =
                activity.action === IActivityLogResponseAction.DisconnectedRexListing;
              const [message, reason] =
                disconnected && activity.activityMessage
                  ? activity.activityMessage.split(/\nReason: (.*)/s)
                  : [activity.activityMessage];
              return (
                <div className="activityItem" key={index}>
                  <AgxSpacer direction="horizontal" gap="spacing-md" padding="spacing-xl" fill>
                    {disconnected ? (
                      <Icon_Link color={tokens.text.secondary} width={17} height={17} />
                    ) : (
                      <Icon_Email color={tokens.text.secondary} width={17} height={17} />
                    )}
                    <AgxSpacer
                      direction="horizontal"
                      gap="spacing-lg"
                      contentJustification="space-between"
                      wrap
                      fill
                    >
                      <AgxSpacer gap="spacing-sm">
                        <AgxTypography id={`${id}-message`} type="body_ui" size="small">
                          {activity.actorName?.firstName} {activity.actorName?.lastName}{' '}
                          <strong>{activity.action && actionLabels[activity.action]}</strong>{' '}
                          {message}
                          {activity.recipient && (
                            <>
                              {' '}
                              <span className="tagTEST">
                                {activity.recipient.recipientName ??
                                  activity.recipient.emailAddress}
                              </span>
                            </>
                          )}
                        </AgxTypography>
                        {reason && (
                          <AgxTypography id={`${id}-reason`} type="body_ui" size="small">
                            <strong>Reason:</strong>{' '}
                            <span style={{ whiteSpace: 'pre-line' }}>{reason}</span>
                          </AgxTypography>
                        )}
                      </AgxSpacer>
                      {activity.activityDate && (
                        <AgxTypography
                          id={`${id}-date`}
                          type="body_ui"
                          size="small"
                          color={tokens.text.secondary}
                          noWrap
                        >
                          {dayjs(activity.activityDate).format('D MMM YYYY, h:mm a')}
                        </AgxTypography>
                      )}
                    </AgxSpacer>
                  </AgxSpacer>
                </div>
              );
            })}
          </AgxSpacer>
        </AgxSpacer>
      </ContentLayout>
    </PageLayout>
  );
};

export default ActivityLogView;
